import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLButtonElement[]} [dstrezzedThumbnail] - The Dstrezzed thumbnail buttons.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupDstrezzedThumbnails();
    this.#setupDstrezzedHoverVisibility();
    this.#setupVideoAutoplay();
  }

  #controller = new AbortController();
  /** @type {IntersectionObserver | undefined} */
  #dstrezzedObserver;
  /** @type {IntersectionObserver | undefined} */
  #videoAutoplayObserver;
  #dstrezzedScrollFrame = 0;
  #dstrezzedPointerX = -1;
  #dstrezzedPointerY = -1;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#dstrezzedObserver?.disconnect();
    this.#videoAutoplayObserver?.disconnect();
    this.#cancelDstrezzedScrollFrame();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    morph(this, newMediaGallery);
    // childrenOnly morph does not call updatedCallback on this root; refresh refs + observers.
    this.updatedCallback();
  };

  /**
   * Called after Section Rendering / morph updates children.
   * Rebinds Dstrezzed thumbnail + video observers to the live media nodes.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#refreshDstrezzedAfterMorph();
  }

  #refreshDstrezzedAfterMorph() {
    this.#dstrezzedObserver?.disconnect();
    this.#videoAutoplayObserver?.disconnect();
    this.#setupVideoAutoplay();
    this.#observeDstrezzedMedia();
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * Scrolls to a grid media item and updates thumbnail state.
   * @param {number} index - The index of the media to focus.
   * @param {Event} [event] - Optional click event from thumbnail button.
   */
  scrollToMedia(index, event) {
    event?.preventDefault();
    const mediaIndex = Number(index);
    if (Number.isNaN(mediaIndex)) return;

    const media = this.#dstrezzedMedia[mediaIndex];
    if (!media) return;

    media.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    this.#setActiveDstrezzedThumbnail(mediaIndex);
  }

  #setupVideoAutoplay() {
    if (!window.IntersectionObserver) return;

    const videoContainers = [...this.querySelectorAll('.media-gallery__grid > .product-media-container--video')];
    if (!videoContainers.length) return;

    this.#videoAutoplayObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const deferredMedia = entry.target.querySelector('deferred-media');
          if (!deferredMedia) continue;

          if (entry.isIntersecting) {
            deferredMedia.loadContent(false);
            deferredMedia.playMedia();
          } else {
            deferredMedia.pauseMedia();
          }
        }
      },
      { threshold: 0.5 }
    );

    for (const container of videoContainers) {
      this.#videoAutoplayObserver.observe(container);
    }
  }

  #setupDstrezzedThumbnails() {
    if (!this.classList.contains('media-gallery--dstrezzed')) return;
    if (!this.#dstrezzedMedia.length) return;
    if (!this.refs.dstrezzedThumbnail?.length) return;

    const { signal } = this.#controller;

    this.#observeDstrezzedMedia();

    window.addEventListener('scroll', this.#handleDstrezzedScroll, { signal, passive: true });
    window.addEventListener('resize', this.#handleDstrezzedScroll, { signal, passive: true });
  }

  #observeDstrezzedMedia() {
    if (!this.classList.contains('media-gallery--dstrezzed')) return;
    if (!this.#dstrezzedMedia.length) return;
    if (!this.refs.dstrezzedThumbnail?.length) return;

    this.#setActiveDstrezzedThumbnail(0);
    this.#setActiveDstrezzedThumbnailByScrollPosition();

    if (!window.IntersectionObserver) return;

    this.#dstrezzedObserver?.disconnect();
    this.#dstrezzedObserver = new IntersectionObserver(this.#handleDstrezzedIntersection, {
      root: null,
      threshold: [0, 0.01, 0.1, 0.25],
      rootMargin: '-10% 0px -55% 0px',
    });

    for (const mediaItem of this.#dstrezzedMedia) {
      this.#dstrezzedObserver.observe(mediaItem);
    }
  }

  #setupDstrezzedHoverVisibility() {
    if (!this.classList.contains('media-gallery--dstrezzed')) return;

    const { signal } = this.#controller;

    window.addEventListener('pointermove', this.#handleDstrezzedPointerMove, { signal, passive: true });
    window.addEventListener('scroll', this.#updateDstrezzedHoverState, { signal, passive: true });
    window.addEventListener('resize', this.#updateDstrezzedHoverState, { signal, passive: true });
    this.addEventListener('pointerenter', this.#handleDstrezzedPointerEnter, { signal });
    this.addEventListener('pointerleave', this.#handleDstrezzedPointerLeave, { signal });
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleDstrezzedIntersection = (entries) => {
    let activeEntry = null;

    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      if (!activeEntry || entry.intersectionRatio > activeEntry.intersectionRatio) {
        activeEntry = entry;
      }
    }

    if (!activeEntry) return;

    const target = activeEntry.target;
    if (!(target instanceof HTMLElement)) return;

    const mediaIndex = Number(target.dataset.mediaIndex);
    if (Number.isNaN(mediaIndex) || mediaIndex < 0) return;

    this.#setActiveDstrezzedThumbnail(mediaIndex);
  };

  #handleDstrezzedScroll = () => {
    if (this.#dstrezzedScrollFrame) return;

    this.#dstrezzedScrollFrame = requestAnimationFrame(() => {
      this.#dstrezzedScrollFrame = 0;
      this.#setActiveDstrezzedThumbnailByScrollPosition();
    });
  };

  #setActiveDstrezzedThumbnailByScrollPosition() {
    if (!this.#dstrezzedMedia.length) return;

    const viewportHeight = window.innerHeight;
    const targetY = viewportHeight * 0.35;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const mediaItem of this.#dstrezzedMedia) {
      if (!(mediaItem instanceof HTMLElement)) continue;

      const mediaIndex = Number(mediaItem.dataset.mediaIndex);
      if (Number.isNaN(mediaIndex)) continue;

      const rect = mediaItem.getBoundingClientRect();
      const distance = Math.abs(rect.top - targetY);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = mediaIndex;
      }
    }

    this.#setActiveDstrezzedThumbnail(closestIndex);
  }

  /**
   * @param {number} activeIndex
   */
  #setActiveDstrezzedThumbnail(activeIndex) {
    if (!this.refs.dstrezzedThumbnail?.length) return;

    for (const [index, thumbnail] of this.refs.dstrezzedThumbnail.entries()) {
      const isActive = index === activeIndex;
      thumbnail.classList.toggle('is-active', isActive);

      if (isActive) {
        thumbnail.setAttribute('aria-current', 'true');
        thumbnail.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #handleDstrezzedPointerMove = (event) => {
    this.#dstrezzedPointerX = event.clientX;
    this.#dstrezzedPointerY = event.clientY;

    this.#updateDstrezzedHoverState();
  };

  /**
   * @param {PointerEvent} event
   */
  #handleDstrezzedPointerEnter = (event) => {
    this.#dstrezzedPointerX = event.clientX;
    this.#dstrezzedPointerY = event.clientY;

    this.#updateDstrezzedHoverState();
  };

  #handleDstrezzedPointerLeave = () => {
    this.classList.remove('is-dstrezzed-hover-active');
  };

  #updateDstrezzedHoverState = () => {
    if (!this.classList.contains('media-gallery--dstrezzed')) return;

    const isInViewport = this.#isDstrezzedInViewport();
    const isPointerInside = this.#isPointerInsideDstrezzed();
    this.classList.toggle('is-dstrezzed-hover-active', isInViewport && isPointerInside);
  };

  #isDstrezzedInViewport() {
    const rect = this.getBoundingClientRect();

    return rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  #isPointerInsideDstrezzed() {
    if (this.#dstrezzedPointerX < 0 || this.#dstrezzedPointerY < 0) return false;

    const rect = this.getBoundingClientRect();

    return (
      this.#dstrezzedPointerX >= rect.left &&
      this.#dstrezzedPointerX <= rect.right &&
      this.#dstrezzedPointerY >= rect.top &&
      this.#dstrezzedPointerY <= rect.bottom
    );
  }

  #cancelDstrezzedScrollFrame() {
    if (!this.#dstrezzedScrollFrame) return;

    cancelAnimationFrame(this.#dstrezzedScrollFrame);
    this.#dstrezzedScrollFrame = 0;
  }

  get #dstrezzedMedia() {
    return [...this.querySelectorAll('.media-gallery__grid > .product-media-container[data-media-index]')];
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    if (this.presentation !== 'grid') return this.refs.media;

    return [...this.querySelectorAll('.media-gallery__grid > .product-media-container')];
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
